import {
  useForm,
  FormProvider,
  DefaultValues,
  FieldValues,
  UseFormWatch,
  useFormContext,
} from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import Button from "@ui-kit/form/Button/Button.web";
import React, { ReactNode, useMemo, useId } from "react";
import Flex, {
  FlexProps,
} from "@shared/components/ui-kit/layout/Flex/Flex.web";
import Card from "@ui-kit/data-display/Card/Card.web";
import Text from "@ui-kit/typography/Text/Text.web";
import Box, { type BoxProps } from "@ui-kit/layout/Box/Box.web";
import { NotifierFactory } from "@shared/services/api/abstracts/notifiers/NotifierFactory";
import { useTranslation } from "@shared/utils/internationalization";
import FormField from "../FormField/FormField.web";
import FormSection from "../FormSection/FormSection.web";
import {
  FormFieldConfig,
  SingleFieldConfig,
  SectionFormFieldConfig,
  CustomFormFieldConfig,
  FieldArrayFormFieldConfig,
  RepeaterFormFieldConfig,
  FORM_FIELD_TYPES,
} from "../FormField/types";
import { createValidationSchema } from "../utils/validation";

/**
 * Shared context for the Form.Root compound component pattern.
 * Lets FormProvider wrap layout components while the <form> tag
 * and footer live at different levels of the tree.
 */
const FormRootContext = React.createContext<{
  formId: string;
  handleFormSubmit: (e?: React.BaseSyntheticEvent) => Promise<void>;
  fieldsArray: FormFieldConfig[];
} | null>(null);

/**
 * Recursively flattens nested fields into a flat array of single fields, field arrays, and repeaters.
 * Used for validation schema and default values extraction.
 * Handles SECTION, GROUP, and skips CUSTOM types.
 */
function flattenFields(
  fields: FormFieldConfig[]
): (SingleFieldConfig | FieldArrayFormFieldConfig | RepeaterFormFieldConfig)[] {
  const flattened: (
    | SingleFieldConfig
    | FieldArrayFormFieldConfig
    | RepeaterFormFieldConfig
  )[] = [];

  const processField = (field: FormFieldConfig) => {
    // CUSTOM types don't have form values
    if (field.type === FORM_FIELD_TYPES.CUSTOM) {
      return;
    }
    // SECTION contains nested fields - recurse into them
    if (field.type === FORM_FIELD_TYPES.SECTION) {
      field.fields.forEach(processField);
      return;
    }
    // GROUP contains SingleFieldConfig[] - spread them
    if (field.type === FORM_FIELD_TYPES.GROUP) {
      flattened.push(...field.fields);
      return;
    }
    // FIELD_ARRAY: keep as-is (it's a single field containing an array)
    if (field.type === FORM_FIELD_TYPES.FIELD_ARRAY) {
      flattened.push(field);
      return;
    }
    // REPEATER: keep as-is (it's a single field containing an array)
    if (field.type === FORM_FIELD_TYPES.REPEATER) {
      flattened.push(field as RepeaterFormFieldConfig);
      return;
    }
    // Single field
    flattened.push(field as SingleFieldConfig);
  };

  fields.forEach(processField);
  return flattened;
}

const getFieldDefaultValue = (
  field: SingleFieldConfig | FieldArrayFormFieldConfig | RepeaterFormFieldConfig
): unknown => {
  if (field.type === FORM_FIELD_TYPES.FIELD_ARRAY) {
    const { minItems = 1, fields: subFields } =
      field as FieldArrayFormFieldConfig;
    return Array.from({ length: minItems }, () =>
      Object.fromEntries(
        subFields.map((subField) => [
          subField.name,
          subField.defaultValue ?? "",
        ])
      )
    );
  }
  return field.defaultValue ?? "";
};

/**
 * Helper component to avoid TypeScript complexity issues with large union types
 */
function FieldsContainer({
  children,
  fieldsContainerProps,
}: {
  children: ReactNode;
  fieldsContainerProps?: FlexProps;
}): JSX.Element {
  // Use a wrapper div to avoid TypeScript complexity issues with Chakra Flex spread
  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        gap: "16px",
        width: "100%",
        ...((fieldsContainerProps as React.CSSProperties) || {}),
      }}
    >
      {children}
    </div>
  );
}

interface FormRenderProps<T extends FieldValues> {
  /** Watch for value changes */
  watch: UseFormWatch<T>;
  /** Whether the form is currently submitting */
  isSubmitting: boolean;
  /** Reset the form */
  reset: () => void;
}

export interface FormProps<T extends FieldValues> {
  /** Form field configurations - can include SECTION types for Card grouping */
  fields?: Record<string, FormFieldConfig>;
  /**
   * Form submission handler
   * @param data - The form data
   * @param options - Helper functions for post-submission actions (e.g. reset, custom function)
   */
  onSubmit?: (
    data: T,
    options?: { reset?: () => void }
  ) => Promise<void> | void;
  /** Validation mode (default: "onTouched") */
  mode?: "onChange" | "onBlur" | "onTouched" | "onSubmit" | "all";
  /** Render children instead of or in addition to automatic fields */
  children?: ReactNode | ((props: FormRenderProps<T>) => ReactNode);
  translate?: (key: string) => string;

  // UI Props
  submitButtonText?: string;
  submitButtonLoadingText?: string;
  submitButtonTestId?: string;
  hideSubmitButton?: boolean;

  // Container Props
  containerProps?: FlexProps;
  fieldsContainerProps?: FlexProps;
  submitButtonProps?: Partial<
    Omit<React.ComponentProps<typeof Button>, "loadingText">
  >;
  submitButtonContainerProps?: Partial<BoxProps>;
  formAttributes?: React.FormHTMLAttributes<HTMLFormElement> & {
    "data-testid"?: string;
  };

  // Notification messages
  /** Success message to show on successful submission */
  successMessage?: string;
  /** Error message to show on failed submission (defaults to error message from API) */
  errorMessage?: string;
}

export interface FormRootProps<T extends FieldValues> {
  fields?: Record<string, FormFieldConfig>;
  onSubmit?: (
    data: T,
    options?: { reset?: () => void }
  ) => Promise<void> | void;
  mode?: "onChange" | "onBlur" | "onTouched" | "onSubmit" | "all";
  children: ReactNode;
  formId?: string;
  translate?: (key: string) => string;
  successMessage?: string;
  errorMessage?: string;
}

export interface FormContentProps {
  children?: ReactNode;
  formAttributes?: React.FormHTMLAttributes<HTMLFormElement> & {
    "data-testid"?: string;
  };
  containerProps?: FlexProps;
}

export interface FormFooterProps {
  submitText?: string;
  submitLoadingText?: string;
  cancelText?: string;
  onCancel?: () => void;
  children?: ReactNode;
  formId?: string;
  "data-testid"?: string;
}

/**
 * Helper component to render custom fields with watch access
 */
function CustomFieldRenderer({ config }: { config: CustomFormFieldConfig }) {
  const { watch } = useFormContext();
  if (config.hidden) return null;
  return (
    <Box width={config.width}>
      {config.render({ watch: (name: string) => watch(name) })}
    </Box>
  );
}

/**
 * Renders a section's child fields with layout support.
 */
function SectionFieldsRenderer({
  fields: sectionFields,
  sectionIndex,
  layout,
}: {
  fields: SectionFormFieldConfig["fields"];
  sectionIndex: number;
  layout?: SectionFormFieldConfig["layout"];
}): JSX.Element {
  const { direction = "column" } = layout || {};

  return (
    <>
      {sectionFields.map((childConfig, childIdx) => {
        const { width, name } = childConfig as SingleFieldConfig;
        const childKey = name || `section-${sectionIndex}-field-${childIdx}`;

        let childElement: React.ReactNode;
        if (childConfig.type === FORM_FIELD_TYPES.CUSTOM) {
          childElement = (
            <CustomFieldRenderer
              config={childConfig as CustomFormFieldConfig}
            />
          );
        } else {
          const fieldKey =
            childConfig.type === FORM_FIELD_TYPES.GROUP
              ? `group-${childIdx}`
              : (childConfig as SingleFieldConfig).name;
          childElement = <FormField key={fieldKey} config={childConfig} />;
        }

        return (
          <Box
            key={childKey}
            width={width || (direction === "column" ? "100%" : undefined)}
            flex={!width && direction === "row" ? 1 : undefined}
          >
            {childElement}
          </Box>
        );
      })}
    </>
  );
}

/**
 * Renders a single field config entry — handles CUSTOM, GROUP, SECTION, and regular fields.
 * Single shared implementation used by both standalone <Form> and <Form.Content>.
 */
function FieldRenderer({
  config,
  idx,
  isSubmitting,
}: {
  config: FormFieldConfig;
  idx: number;
  isSubmitting: boolean;
}): JSX.Element | null {
  if (config.type === FORM_FIELD_TYPES.CUSTOM) {
    return (
      <CustomFieldRenderer
        key={`custom-${idx}`}
        config={config as CustomFormFieldConfig}
      />
    );
  }
  if (config.type === FORM_FIELD_TYPES.SECTION) {
    const sectionConfig = config as SectionFormFieldConfig;
    if (sectionConfig.hidden) return null;

    if (sectionConfig.previewMode) {
      return (
        <FormSection
          key={`section-${idx}`}
          config={sectionConfig}
          sectionIndex={idx}
          isSubmitting={isSubmitting}
          renderFields={(fields, sectionIdx) => (
            <SectionFieldsRenderer
              fields={fields}
              sectionIndex={sectionIdx}
              layout={sectionConfig.layout}
            />
          )}
        />
      );
    }

    const { direction = "column", gap = "16px" } = sectionConfig.layout || {};

    return (
      <Card key={`section-${idx}`}>
        {sectionConfig.title && (
          <Text fontSize="lg" fontWeight="semibold" mb="2xs">
            {sectionConfig.title}
          </Text>
        )}
        {sectionConfig.description && (
          <Text fontSize="sm" color="gray.600" mb="md">
            {sectionConfig.description}
          </Text>
        )}
        <Flex
          direction={direction}
          gap={gap}
          flexWrap="wrap"
          opacity={isSubmitting ? 0.6 : 1}
          pointerEvents={isSubmitting ? "none" : "auto"}
        >
          <SectionFieldsRenderer
            fields={sectionConfig.fields}
            sectionIndex={idx}
            layout={sectionConfig.layout}
          />
        </Flex>
      </Card>
    );
  }
  const key =
    config.type === FORM_FIELD_TYPES.GROUP
      ? `group-${idx}`
      : (config as SingleFieldConfig).name;
  return <FormField key={key} config={config} />;
}

/**
 * Renders an array of field configs. Reads isSubmitting from form context.
 * Single shared implementation used by both standalone <Form> and <Form.Content>.
 */
function FormFieldsRenderer({
  fieldsArray,
}: {
  fieldsArray: FormFieldConfig[];
}): JSX.Element {
  const {
    formState: { isSubmitting },
  } = useFormContext();

  return (
    <>
      {fieldsArray.map((config, idx) => {
        const getKey = (): string => {
          if (config.type === FORM_FIELD_TYPES.SECTION) return `section-${idx}`;
          if (config.type === FORM_FIELD_TYPES.GROUP) return `group-${idx}`;
          if (config.type === FORM_FIELD_TYPES.CUSTOM) return `custom-${idx}`;
          return (config as SingleFieldConfig).name;
        };

        return (
          <FieldRenderer
            key={getKey()}
            config={config}
            idx={idx}
            isSubmitting={isSubmitting}
          />
        );
      })}
    </>
  );
}

/**
 * Form Component (The Form Provider)
 *
 * This component initializes the form context and handles submission.
 * It can render fields from config OR children components that consume the context.
 */
export default function Form<T extends FieldValues>({
  fields = {},
  onSubmit,
  mode = "onTouched",
  children,
  translate,
  submitButtonText = "Save",
  submitButtonLoadingText = "Saving...",
  submitButtonTestId,
  hideSubmitButton = false,
  containerProps,
  fieldsContainerProps,
  submitButtonProps,
  submitButtonContainerProps,
  formAttributes,
  successMessage,
  errorMessage,
}: FormProps<T>) {
  const notifier = NotifierFactory.createNotifier();
  const fieldsArray = useMemo(() => Object.values(fields), [fields]);

  // Flatten fields for validation and default values (excludes GROUP containers)
  const flattenedFields = useMemo(
    () => flattenFields(fieldsArray),
    [fieldsArray]
  );

  const defaultValues = useMemo(
    () =>
      Object.fromEntries(
        flattenedFields.map((field) => [
          field.name,
          getFieldDefaultValue(field),
        ])
      ) as DefaultValues<T>,
    [flattenedFields]
  );

  const validationSchema =
    flattenedFields.length > 0
      ? createValidationSchema(flattenedFields, translate)
      : undefined;

  const form = useForm<T>({
    resolver: validationSchema ? yupResolver(validationSchema) : undefined,
    mode,
    defaultValues,
  });

  const handleSubmit = async (data: T) => {
    if (onSubmit) {
      try {
        await onSubmit(data, { reset: () => form.reset() });
        if (successMessage) {
          notifier.success(successMessage);
        }
      } catch (error) {
        const message =
          errorMessage ||
          (error instanceof Error ? error.message : "An error occurred");
        notifier.error(message);
        // Don't re-throw - let the form reset isSubmitting state
      }
    }
  };

  const { isSubmitting } = form.formState;

  const renderContent = () => {
    if (typeof children === "function") {
      return children({ watch: form.watch, isSubmitting, reset: form.reset });
    }
    if (children) {
      return children;
    }

    return (
      <FieldsContainer fieldsContainerProps={fieldsContainerProps}>
        <FormFieldsRenderer fieldsArray={fieldsArray} />
      </FieldsContainer>
    );
  };

  const renderSubmitButton = () => {
    if (children || hideSubmitButton) return null;

    const submitButton = (
      <Form.SubmitButton
        data-testid={submitButtonTestId || "form-submit-button"}
        text={submitButtonText}
        loadingText={submitButtonLoadingText}
        {...submitButtonProps}
      />
    );

    return submitButtonContainerProps ? (
      <Box {...submitButtonContainerProps}>{submitButton}</Box>
    ) : (
      submitButton
    );
  };

  return (
    <FormProvider {...form}>
      <form
        onSubmit={form.handleSubmit(handleSubmit)}
        {...formAttributes}
        style={{ width: "100%" }}
        noValidate
      >
        <Flex
          direction="column"
          gap="md"
          width="100%"
          {...(containerProps as FlexProps)}
        >
          {renderContent()}

          {renderSubmitButton()}
        </Flex>
      </form>
    </FormProvider>
  );
}

Form.SubmitButton = function FormSubmitButton({
  text,
  loadingText,
  "data-testid": dataTestId,
  ...props
}: Omit<React.ComponentProps<typeof Button>, "loadingText"> & {
  text?: string;
  loadingText?: string;
  "data-testid"?: string | number;
}) {
  const {
    formState: { isSubmitting },
  } = useFormContext();

  return (
    <Flex justifyContent="flex-end">
      <Button
        variant="solid"
        colorPalette="primary"
        minWidth="element.8xl"
        type="submit"
        mt="xs" // 16px from gap + 8px from margin
        isLoading={isSubmitting}
        loadingText={loadingText}
        isDisabled={isSubmitting}
        data-testid={dataTestId || "form-submit-button"}
        {...props}
      >
        {text || "Submit"}
      </Button>
    </Flex>
  );
};

/**
 * Form.Root — Provides FormProvider without rendering a <form> tag.
 * Use with Form.Content and Form.Footer to split form context from the
 * form element, enabling the footer to render outside the scrollable area.
 *
 * @example
 * <Form.Root fields={FIELDS} onSubmit={handleSubmit}>
 *   <DashboardPageHeader footer={<Form.Footer submitText="Save" />}>
 *     <Form.Content>
 *       <MyFormFields />
 *     </Form.Content>
 *   </DashboardPageHeader>
 * </Form.Root>
 */
Form.Root = function FormRoot<T extends FieldValues>({
  fields = {},
  onSubmit,
  mode = "onTouched",
  children,
  formId: formIdProp,
  translate,
  successMessage,
  errorMessage,
}: FormRootProps<T>): JSX.Element {
  const notifier = NotifierFactory.createNotifier();
  const autoFormId = useId();
  const formId = formIdProp || autoFormId;

  const fieldsArray = useMemo(() => Object.values(fields), [fields]);
  const flattenedFields = useMemo(
    () => flattenFields(fieldsArray),
    [fieldsArray]
  );

  const defaultValues = useMemo(
    () =>
      Object.fromEntries(
        flattenedFields.map((field) => [
          field.name,
          getFieldDefaultValue(field),
        ])
      ) as DefaultValues<T>,
    [flattenedFields]
  );

  const validationSchema =
    flattenedFields.length > 0
      ? createValidationSchema(flattenedFields, translate)
      : undefined;

  const form = useForm<T>({
    resolver: validationSchema ? yupResolver(validationSchema) : undefined,
    mode,
    defaultValues,
  });

  const handleFormSubmit = form.handleSubmit(async (data: T) => {
    if (onSubmit) {
      try {
        await onSubmit(data, { reset: () => form.reset() });
        if (successMessage) {
          notifier.success(successMessage);
        }
      } catch (error) {
        const message =
          errorMessage ||
          (error instanceof Error ? error.message : "An error occurred");
        notifier.error(message);
      }
    }
  });

  return (
    <FormRootContext.Provider value={{ formId, handleFormSubmit, fieldsArray }}>
      <FormProvider {...form}>{children}</FormProvider>
    </FormRootContext.Provider>
  );
};

/**
 * Form.Content — Renders the <form> tag. Must be a descendant of Form.Root.
 * Place inside the scrollable area while Form.Footer sits outside it.
 *
 * When no children are provided, auto-renders fields from the Form.Root context
 * (including SECTION types as Card groups). This lets pages pass only JSON config
 * without manually looping over fields.
 */
Form.Content = function FormContent({
  children,
  formAttributes,
  containerProps,
}: FormContentProps): JSX.Element {
  const rootContext = React.useContext(FormRootContext);
  if (!rootContext) {
    throw new Error("Form.Content must be used within Form.Root");
  }
  const { formId, handleFormSubmit, fieldsArray } = rootContext;

  return (
    <form
      id={formId}
      onSubmit={handleFormSubmit}
      {...formAttributes}
      style={{ width: "100%" }}
      noValidate
    >
      <Flex
        direction="column"
        gap="md"
        width="100%"
        {...(containerProps as FlexProps)}
      >
        {children || <FormFieldsRenderer fieldsArray={fieldsArray} />}
      </Flex>
    </form>
  );
};

/**
 * Form.Footer — Sticky footer bar with submit/cancel buttons.
 * Reads form state via useFormContext() and submits via the HTML form attribute.
 * Works inside Form.Root (auto-resolves formId) or with any FormProvider
 * when an explicit formId prop is passed.
 */
Form.Footer = function FormFooter({
  submitText,
  submitLoadingText,
  cancelText,
  onCancel,
  children,
  formId: formIdProp,
  "data-testid": dataTestId,
}: FormFooterProps): JSX.Element {
  const { t } = useTranslation("common");
  const rootContext = React.useContext(FormRootContext);
  const resolvedFormId = formIdProp || rootContext?.formId;
  if (!resolvedFormId) {
    throw new Error(
      "Form.Footer requires a formId prop or must be used within Form.Root"
    );
  }

  const {
    formState: { isSubmitting },
  } = useFormContext();

  const footerProps = {
    bg: "bg" as const,
    borderTop: "1px solid",
    borderTopColor: "border",
    p: "xl" as const,
    alignItems: "center" as const,
    flexDirection: "row" as const,
    flexShrink: 0,
    "data-testid": dataTestId || "form-footer",
  };

  if (children) {
    return (
      <Flex {...footerProps} justifyContent="space-between">
        {children}
      </Flex>
    );
  }

  return (
    <Flex
      {...footerProps}
      justifyContent={onCancel ? "space-between" : "flex-end"}
    >
      {onCancel && (
        <Button
          variant="ghost"
          onClick={onCancel}
          data-testid="form-footer-cancel"
        >
          {cancelText || t("Cancel")}
        </Button>
      )}
      <Button
        type="submit"
        form={resolvedFormId}
        variant="solid"
        colorPalette="primary"
        isLoading={isSubmitting}
        loadingText={submitLoadingText}
        isDisabled={isSubmitting}
        data-testid="form-footer-submit"
      >
        {submitText || t("Save")}
      </Button>
    </Flex>
  );
};
